import { highlightPoiLink, poiToHikeHighlight, HikeHighlightPayload } from "../../../utils/helpers/PoiToHikeHighlightHelper";
import { haversineMeters } from "./stage-connectors";
import { DESTINATION_ATTACHED_THRESHOLD_M, parsePoiItemId, StageDestination } from "./stage-destinations";

/**
 * A night pinned to a place (a hotel, a hut, a campsite) becomes one of that
 * day's hike highlights too, linked back to the place — so it shows on the
 * hike and in the apps like any other highlight. Only places count: a pinned
 * waypoint or highlight is already the user's own.
 */

interface HighlightLike {
  clientId?: string;
  title?: string;
  description?: string | null;
  coordinates?: number[];
  dayNumber?: number | null;
  poiType?: string;
  poiId?: string | number;
  images?: unknown[];
  imageSignedIds?: unknown[];
  media?: unknown[];
  mediaSignedIds?: unknown[];
}

const sameName = (a?: string, b?: string) => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/**
 * The highlight to add for `destination` on day `dayNumber`, or null when it
 * isn't a place or the plan already has a highlight for it (linked to the same
 * place, or with the same name in the same spot).
 */
export function highlightForDestination(
  destination: StageDestination,
  dayNumber: number | null | undefined,
  highlights: HighlightLike[]
): (HikeHighlightPayload & { dayNumber?: number }) | null {
  if (destination.itemType !== 'poi') return null;
  const parsed = parsePoiItemId(destination.itemId);
  const link = highlightPoiLink(parsed?.categoryType, parsed?.poiId);

  const already = highlights.some(highlight =>
    (link && highlight.poiType === link.poiType && String(highlight.poiId) === link.poiId) ||
    (!!highlight.coordinates &&
      haversineMeters(highlight.coordinates, destination.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M &&
      sameName(highlight.title, destination.name)));
  if (already) return null;

  const [lng, lat] = destination.coordinates;
  return {
    ...poiToHikeHighlight({
      type: destination.poiType, name: destination.name, lng, lat, poiClass: parsed?.categoryType, poiId: parsed?.poiId,
    }),
    ...(dayNumber != null ? { dayNumber } : {}),
  };
}

/** The plan's highlight for the place `destination` is, if it has one (see highlightForDestination). */
function existingHighlightFor<H extends HighlightLike>(destination: StageDestination, highlights: H[]): H | null {
  if (destination.itemType === 'highlight') return highlights.find(highlight => highlight.clientId === destination.itemId) ?? null;
  if (destination.itemType !== 'poi') return null;
  const parsed = parsePoiItemId(destination.itemId);
  const link = highlightPoiLink(parsed?.categoryType, parsed?.poiId);
  // One linked to the place wins over one that only shares its name and spot.
  const linked = link
    ? highlights.find(highlight => highlight.poiType === link.poiType && String(highlight.poiId) === link.poiId)
    : undefined;
  return linked ?? highlights.find(highlight => !!highlight.coordinates &&
    haversineMeters(highlight.coordinates, destination.coordinates) <= DESTINATION_ATTACHED_THRESHOLD_M &&
    sameName(highlight.title, destination.name)) ?? null;
}

export type DestinationHighlightChange =
  | { kind: 'add'; highlight: HikeHighlightPayload & { dayNumber?: number } }
  | { kind: 'move'; clientId: string; dayNumber: number };

/**
 * What pinning day `dayNumber` to `destination` does to the highlights: a
 * place without one gets one; a place that has one already — another day's,
 * say, before the days were re-planned — has it moved to this day. Null when
 * there's nothing to do.
 */
export function destinationHighlightChange(
  destination: StageDestination,
  dayNumber: number | null | undefined,
  highlights: HighlightLike[]
): DestinationHighlightChange | null {
  const existing = existingHighlightFor(destination, highlights);
  if (existing) {
    if (dayNumber == null || existing.dayNumber === dayNumber || !existing.clientId) return null;
    return { kind: 'move', clientId: existing.clientId, dayNumber };
  }
  const highlight = highlightForDestination(destination, dayNumber, highlights);
  return highlight ? { kind: 'add', highlight } : null;
}

const hasAny = (list?: unknown[]) => Array.isArray(list) && list.length > 0;

/** Made for a stop and not added to since: no description, photos or audio. */
function isPlainStopHighlight(highlight: HighlightLike): boolean {
  return !highlight.description?.trim()
    && !hasAny(highlight.images) && !hasAny(highlight.imageSignedIds)
    && !hasAny(highlight.media) && !hasAny(highlight.mediaSignedIds);
}

/**
 * The highlight to drop when a stop pinned to a place (`destination`) is
 * detached, dragged away or pinned somewhere else: the plain highlight that
 * was made for the place, unless one of the `remaining` stops still uses it.
 * Null for anything else — a stop on a waypoint or on the walker's own
 * highlight never made one, and a highlight someone has written about or
 * added photos to is theirs.
 */
export function detachedStopHighlightId(
  destination: StageDestination | null | undefined,
  remaining: Array<StageDestination | null | undefined>,
  highlights: HighlightLike[]
): string | null {
  if (!destination || destination.itemType !== 'poi') return null;
  const highlight = existingHighlightFor(destination, highlights);
  if (!highlight?.clientId || !isPlainStopHighlight(highlight)) return null;
  const stillUsed = remaining.some(other => !!other && existingHighlightFor(other, highlights)?.clientId === highlight.clientId);
  return stillUsed ? null : highlight.clientId;
}

/**
 * The highlights that are just the plan's pinned stops — made for a stop and
 * not added to since (no description, photos or audio) — which re-planning
 * the days clears away with the stops themselves. Highlights someone has
 * written about or added photos to are theirs, and stay.
 */
export function stopHighlightIds(
  destinations: { [day: number]: StageDestination } | null | undefined,
  highlights: HighlightLike[]
): string[] {
  const ids = new Set<string>();
  Object.values(destinations ?? {}).forEach(destination => {
    const highlight = existingHighlightFor(destination, highlights);
    if (highlight?.clientId && isPlainStopHighlight(highlight)) ids.add(highlight.clientId);
  });
  return [...ids];
}

/** The plan's highlight for a pinned stop's place (or the stop's own highlight), if it has one. */
export function stopHighlight<H extends HighlightLike>(destination: StageDestination | null | undefined, highlights: H[]): H | null {
  return destination ? existingHighlightFor(destination, highlights) : null;
}

/**
 * The highlights that are a day's ends — the place its stop is pinned to,
 * where it started from — which the day's list of highlights leaves out:
 * they're already its Start and End rows.
 */
export function endpointHighlightIds(
  destinations: Array<StageDestination | null | undefined>,
  highlights: HighlightLike[]
): Set<string> {
  const ids = new Set<string>();
  destinations.forEach(destination => {
    const highlight = destination ? existingHighlightFor(destination, highlights) : null;
    if (highlight?.clientId) ids.add(highlight.clientId);
  });
  return ids;
}
